import React from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { classNames } from 'utils/styleUtils';
import { ISchemaLevelObjectType } from 'utils/types';
import ObjectNode from './ObjectNode';
import SnowflakeIcon from 'components/SnowflakeIcon';

interface SchemaNodeProps {
  name: string;
  data: any;
}

export default function SchemaNode(props: SchemaNodeProps): JSX.Element {
  const { name, data } = props;
  // Define all possible object types
  const objectTypes: ISchemaLevelObjectType[] = [
    'table',
    'view',
    'function',
    'procedure',
    'file_format',
    'sequence',
    'pipe',
  ];

  // Get total count of all objects
  const getTotalObjectCount = (): number => {
    let count = 0;

    objectTypes.forEach((type) => {
      // Handle both singular and plural forms in the data structure
      const pluralKey = `${type}s`;
      const singularKey = type;

      if (data[pluralKey] !== undefined && Object.keys(data[pluralKey]).length > 0) {
        count += Object.keys(data[pluralKey]).length;
      } else if (data[singularKey] !== undefined && Object.keys(data[singularKey]).length > 0) {
        count += Object.keys(data[singularKey]).length;
      }
    });

    return count;
  };

  const totalObjects = getTotalObjectCount();
  const hasObjects = totalObjects > 0;

  return (
    <Disclosure defaultOpen={true}>
      {({ open }) => (
        <>
          <div className="w-full relative flex py-1 border border-gray-300 bg-gray-50 rounded-md text-gray-700 cursor-default px-2">
            <div className="min-w-[20px] w-5 mr-1">
              <SnowflakeIcon objectType="schema" highlighted={false} />
            </div>
            <div className="flex-1 flex items-center justify-between">
              <div className="max-w-[9rem] text-sm truncate">{name}</div>
              {/* Display the total number of objects if there's more than one */}
              {totalObjects > 1 && (
                <div className="text-xs text-gray-500 ml-2 mr-4 whitespace-nowrap">{totalObjects} objects</div>
              )}
            </div>
            <DisclosureButton className="absolute right-1 w-5 cursor-pointer">
              <ChevronRightIcon
                className={classNames(
                  open ? 'transform rotate-90' : '',
                  'w-5 h-5 ml-auto text-gray-700 transition duration-150 ease-in-out',
                )}
              />
            </DisclosureButton>
          </div>

          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <DisclosurePanel className="px-4 space-y-1">
              {hasObjects ? (
                <>
                  {/* Render all object types dynamically */}
                  {objectTypes.map((type) => {
                    // Check for both singular and plural forms
                    const pluralKey = `${type}s`;
                    const singularKey = type;

                    // Determine which key to use
                    let objectsKey = '';
                    let objects = null;

                    if (data[pluralKey] !== undefined && Object.keys(data[pluralKey]).length > 0) {
                      objectsKey = pluralKey;
                      objects = data[pluralKey];
                    } else if (data[singularKey] !== undefined && Object.keys(data[singularKey]).length > 0) {
                      objectsKey = singularKey;
                      objects = data[singularKey];
                    }

                    // If we found objects of this type, render them
                    if (objectsKey !== '' && objects !== null) {
                      return Object.entries(objects).map(([objectName, objectData]: [string, any]) => (
                        <ObjectNode
                          key={`${type}-${objectName}`}
                          name={objectName}
                          data={objectData}
                          objectType={type}
                        />
                      ));
                    }

                    return null;
                  })}
                </>
              ) : (
                <div className="mb-2 text-sm text-gray-500">No objects found</div>
              )}
            </DisclosurePanel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}
